import type { FieldApi, Snapshot } from "../../../lib/api/fieldApi";
import { latestSnapshot } from "../../../lib/api/fieldApi";
import { defaultProject, visibleImports, visibleProjects } from "./checks";

/**
 * Campus 미션 입장 자동 올리기(2026-11-03 사용자 요청) — 놀이터 → 미션 하기 → 새로 올리기 → 다음 → 올리기 → 입구로 → 코스 도전을 한 번에.
 * 최근 Campus 조립(가장 최근에 받은 것)과 그 짝 Bench 저장본(defaultProject — 올리기 화면의 기본 짝과 같다)을 확인하고 올린다.
 *
 * - `uploaded` — 새로 올렸다 · `same` — 마지막 작품이 이미 같은 조립 · 프로젝트이고 그 뒤로 바뀐 것이 없다(다시 올리지 않는다).
 * - `none` — 받은 조립이 없거나 짝 Bench 저장본이 없다 · `invalid` — 검사에서 걸렸다(올리기 화면에서 이유를 보인다).
 */
export type AutoUploadResult =
  | { kind: "uploaded" | "same"; snapshot: Snapshot | null }
  | { kind: "none" | "invalid" };

const time = (s: string | null | undefined) => (s ? Date.parse(s) || 0 : 0);

export async function autoUploadLatest(api: FieldApi): Promise<AutoUploadResult> {
  const sources = await api.sources();
  let imp = visibleImports(sources.campus)[0] ?? null;
  if (!imp) return { kind: "none" };
  // 기한이 지난 조립은 Campus 에서 다시 받는다(올리기 화면의 '다시 받기'와 같다)
  if (imp.status === "expired") imp = await api.refreshImport(imp.importId).catch(() => null);
  if (!imp || imp.status !== "ready") return { kind: "none" };
  const projects = visibleProjects(sources.bench);
  const projectId = defaultProject(imp, projects);
  const project = projects.find((p) => p.projectId === projectId) ?? null;
  // 미션은 프로그램으로 달린다 — 저장한 코딩이 없으면 올리기 화면에서 고르게 한다
  if (!project) return { kind: "none" };

  const last = latestSnapshot(await api.mySnapshots().catch(() => []));
  const changedAt = Math.max(time(imp.refetchedAt ?? imp.receivedAt), time(project.updatedAt));
  if (
    last &&
    last.modelSlug === imp.modelSlug &&
    (last.programProjectId ?? null) === project.projectId &&
    time(last.createdAt) >= changedAt
  )
    return { kind: "same", snapshot: last };

  const sel = { importId: imp.importId, benchProjectId: project.projectId };
  const check = await api.validate(sel).catch(() => null);
  if (!check?.ok) return { kind: "invalid" };
  const snapshot = await api.createSnapshot(sel).catch(() => null);
  return snapshot ? { kind: "uploaded", snapshot } : { kind: "invalid" };
}
